(() => {
  // Shared Sentry helpers live in sentry-logger.js (loaded first) and are
  // exposed on window. Alias them here for brevity.
  const logToSentry = window.logToSentry;
  const logExceptionToSentry = window.logExceptionToSentry;

  try {
    // Track if the current PWS came from a show URL
    let isFromShowUrl = false;

    // ---------------------------------------------------------------------------
    // Attribution provenance (CAB-3503, CAB-3524)
    //
    // Nothing on an order used to say *why* a stylist was attached —
    // attributes[StylistAttributionSource] was hardcoded to null, which is why
    // misattribution investigations had to reconstruct behaviour from session
    // logs. getCurrentPWS() knows the answer at the moment it decides, so record
    // it and hand it to checkout via global-variables.liquid#customCheckout.
    // ---------------------------------------------------------------------------
    const ATTRIBUTION_SOURCE_KEY = "CURRENT_ATTRIBUTION_SOURCE";
    const ATTRIBUTION_SET_AT_KEY = "CURRENT_ATTRIBUTION_SET_AT";

    const ATTRIBUTION_SOURCES = {
      SHOW: "show",
      SHOW_STORED: "show_stored",
      SHOW_CLOSED_FALLBACK: "show_closed_fallback",
      PWS_PARAM: "pws_param",
      VANITY_PATH: "vanity_path",
      REFERRER: "referrer",
      STORED: "stored",
      STYLIST_SELF: "stylist_self",
      PREFERRED_STYLIST: "preferred_stylist",
    };

    // Source for the PWS currently being resolved. Persisted only once a partner
    // actually resolves, so a failed lookup cannot leave a misleading source.
    let attributionSource = null;

    const setAttributionSource = (source) => {
      attributionSource = source;
    };

    // ---------------------------------------------------------------------------
    // Attribution lifetime (CAB-3520)
    //
    // The keys below have never had an expiry — a link clicked months ago still
    // attributes an order. The mechanism is implemented here, but the TTL is left
    // DISABLED because the business has not agreed a duration yet (CAB-3520 /
    // CAB-2839 discussion: time-based vs visit-based was raised and not settled).
    //
    // To switch it on, set ATTRIBUTION_TTL_MS to a number of milliseconds, e.g.
    //   const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
    // Leaving it null preserves today's behaviour exactly.
    // ---------------------------------------------------------------------------
    const ATTRIBUTION_TTL_MS = null;

    const clearStylistAttribution = () => {
      localStorage.removeItem("CURRENT_SITE");
      localStorage.removeItem("CURRENT_STYLIST_ID");
      localStorage.removeItem("CURRENT_STYLIST_TYPE");
      localStorage.removeItem(ATTRIBUTION_SOURCE_KEY);
      localStorage.removeItem(ATTRIBUTION_SET_AT_KEY);
      // Must go too, or the next resolution of the same site sees an unchanged
      // stamp, never re-writes ATTRIBUTION_SET_AT, and can never expire again.
      localStorage.removeItem("CURRENT_ATTRIBUTED_SITE_STAMP");
      sessionStorage.removeItem("PREFERRED_SITE");
    };

    // ---------------------------------------------------------------------------
    // Terminated stylists (CAB-3494)
    //
    // "Former" is the status the partners API reports for a terminated or
    // pending-termination stylist. They must never be attributed, offered as a
    // choice, labelled "Your Preferred Stylist", or sent to checkout.
    // ---------------------------------------------------------------------------
    const isFormerPartner = (partner) => partner?.status === "Former";

    // ---------------------------------------------------------------------------
    // "Continue without a stylist" opt-out (CAB-3494)
    //
    // The unavailable-stylist dialog is raised from the preferred-stylist
    // metafield, which "Continue Without Stylist" has no way to change. Clearing
    // the CURRENT_* keys was therefore not enough on its own: the next page load
    // resolved the same terminated stylist straight back out of the metafield and
    // reopened the dialog — on every page, on add-to-cart and again at checkout.
    //
    // This flag is what makes the shopper's choice stick. It suppresses ONLY that
    // dialog; an explicit stylist link still attributes normally and clears the
    // flag, since picking a stylist is the opposite of opting out.
    //
    // The writers live in partner-selector.liquid#continueWithoutStylist and the
    // "remove consultant" handler in custom.js — keep the key name and the record
    // shape in step with them.
    // ---------------------------------------------------------------------------
    const STYLIST_OPT_OUT_KEY = "STYLIST_OPT_OUT";

    const hasStylistOptOut = () => {
      try {
        const raw = localStorage.getItem(STYLIST_OPT_OUT_KEY);
        if (!raw) return false;

        const optOut = JSON.parse(raw);
        if (!optOut || optOut.value !== true || !optOut.expires) {
          localStorage.removeItem(STYLIST_OPT_OUT_KEY);
          return false;
        }

        if (Date.now() >= optOut.expires) {
          localStorage.removeItem(STYLIST_OPT_OUT_KEY);
          logToSentry("Stylist opt-out expired - unavailable dialog re-armed", "info");
          return false;
        }

        return true;
      } catch (e) {
        localStorage.removeItem(STYLIST_OPT_OUT_KEY);
        logExceptionToSentry(e, { context: "hasStylistOptOut" });
        return false;
      }
    };

    const clearStylistOptOut = () => {
      if (localStorage.getItem(STYLIST_OPT_OUT_KEY)) {
        localStorage.removeItem(STYLIST_OPT_OUT_KEY);
      }
    };

    const removePwsParamFromUrl = () => {
      const params = new URLSearchParams(window.location.search);
      if (!params.has("pws")) return;
      params.delete("pws");
      const query = params.toString();
      window.history.replaceState(
        {},
        "",
        window.location.pathname + (query ? "?" + query : "") + window.location.hash
      );
    };

    // Drop stored stylist attribution once it is older than the configured TTL.
    // No-op while ATTRIBUTION_TTL_MS is null.
    const expireStaleAttribution = () => {
      if (!ATTRIBUTION_TTL_MS) return;
      const setAt = parseInt(localStorage.getItem(ATTRIBUTION_SET_AT_KEY) || "", 10);
      if (!setAt || Number.isNaN(setAt)) return;
      if (Date.now() - setAt <= ATTRIBUTION_TTL_MS) return;

      logToSentry("Stylist attribution expired - clearing", "info", {
        setAt,
        ageMs: Date.now() - setAt,
        ttlMs: ATTRIBUTION_TTL_MS,
        site: localStorage.getItem("CURRENT_SITE"),
      });
      clearStylistAttribution();
    };

    // ---------------------------------------------------------------------------
    // Show attribution (CAB-2840, CAB-3515, CAB-3516)
    // ---------------------------------------------------------------------------
    const clearShowAttribution = (showId) => {
      logToSentry('Show attribution cleared', 'info', {
        attribution_event: 'show:cleared',
        cleared_show_id: localStorage.getItem("CURRENT_SHOW_ID"),
        cleared_show_site: localStorage.getItem("CURRENT_SHOW_SITE"),
      });
      localStorage.removeItem("CURRENT_SHOW_ID");
      localStorage.removeItem("CURRENT_SHOW_SITE");
      localStorage.removeItem("CURRENT_SHOW_NAME");
      localStorage.removeItem("CURRENT_SHOW_HOSTESS_NAME");
      localStorage.removeItem("CURRENT_SHOW_EVENT_IDS");
      if (showId) {
        const checkInKey = `SHOW_CHECKED_IN_${showId}`;
        sessionStorage.removeItem(checkInKey);
        localStorage.removeItem(checkInKey);
      }
    };

    // Strip ?show= from the address bar without touching anything else.
    const removeShowParamFromUrl = () => {
      const params = new URLSearchParams(window.location.search);
      if (!params.has("show")) return;
      params.delete("show");
      const query = params.toString();
      window.history.replaceState(
        {},
        "",
        window.location.pathname + (query ? "?" + query : "") + window.location.hash
      );
    };

    // Persist the show keys for a validated, open show. Event ids are captured
    // here because backend.js#getShowById deliberately does not cache (so a show
    // closing mid-session is always noticed), leaving checkout no other source for
    // attributes[EventID].
    const persistShowAttribution = (showId, partner, show) => {
      localStorage.setItem("CURRENT_SHOW_ID", showId);
      localStorage.setItem("CURRENT_SHOW_SITE", partner.siteName);
      if (show) {
        if (show.name) localStorage.setItem("CURRENT_SHOW_NAME", show.name);
        if (show.hostessName) localStorage.setItem("CURRENT_SHOW_HOSTESS_NAME", show.hostessName);
        const eventIds = Array.isArray(show.events)
          ? show.events.map((e) => e && e.shareId).filter(Boolean).join(",")
          : "";
        if (eventIds) localStorage.setItem("CURRENT_SHOW_EVENT_IDS", eventIds);
      }
    };

    // Resolve a show and decide whether it may still drive attribution.
    // Returns { isOpen, partner, show }. A closed show yields isOpen:false but
    // still returns the partner so the caller can gracefully fall back to that
    // stylist's PWS instead of dropping attribution entirely.
    const validateShow = async (showId) => {
      if (!showId || !window.backend) return { isOpen: false, partner: null, show: null };

      const [partner, show] = await Promise.all([
        window.backend.getPartnerByShowId(showId),
        window.backend.getShowById(showId),
      ]).catch((err) => {
        logExceptionToSentry(err, { context: "validateShow", showId });
        return [null, null];
      });

      // A show we cannot read is treated as unusable rather than open, so a
      // closed show can never slip through on a failed lookup.
      if (show?.isClosed) {
        logToSentry("Show is closed - clearing show attribution", "info", {
          attribution_event: 'show:closed',
          showId,
          partnerSiteName: partner?.siteName ?? null,
        });
        return { isOpen: false, partner: partner || null, show };
      }

      if (show && partner?.siteName?.length) {
        return { isOpen: true, partner, show };
      }

      return { isOpen: false, partner: partner || null, show: show || null };
    };

    // Public exit hook for the "leave this show" affordance (CAB-3516).
    // Clears show state, drops the URL param, and re-resolves the shopper onto
    // the show stylist's own PWS so they are not left with no attribution.
    window.attributionExitShow = () => {
      const showId = localStorage.getItem("CURRENT_SHOW_ID");
      const showSite = localStorage.getItem("CURRENT_SHOW_SITE");
      logToSentry("Customer exited show attribution", "info", { showId, showSite });
      clearShowAttribution(showId);
      removeShowParamFromUrl();
      document.dispatchEvent(new CustomEvent("theme:show:exited", { detail: { showId, showSite } }));
      return { showId, showSite };
    };

    // Persist the attributed stylist's site to the cart whenever a stylist is
    // selected/resolved by ANY source — direct PWS landing, show link, the
    // stylist selector, find-your-consultant, preferred-stylist sync, etc. All
    // of those dispatch `theme:partner:render` with the resolved partner, so we
    // hook that single event rather than any one selection path. This makes
    // StylistSiteName available on the cart/order for downstream logic such as
    // the denim discount (CAB-3244), which must be redeemable only with the
    // issuing stylist. Skips redundant writes when the site is unchanged.
    const STYLIST_SITE_ATTR = "StylistSiteName";
    const STYLIST_SITE_WRITTEN_KEY = "CART_STYLIST_SITE_WRITTEN";

    const setStylistSiteCartAttribute = async (siteName) => {
      if (!siteName) return;
      try {
        if (localStorage.getItem(STYLIST_SITE_WRITTEN_KEY) === siteName) return;

        const cartUrl =
          (window.theme && window.theme.routes && window.theme.routes.cart) || "/cart";

        const res = await fetch(`${cartUrl}/update.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ attributes: { [STYLIST_SITE_ATTR]: siteName } }),
        });

        if (res.ok) {
          localStorage.setItem(STYLIST_SITE_WRITTEN_KEY, siteName);
        } else {
          logToSentry("Failed to set StylistSiteName cart attribute", "warning", {
            siteName,
            status: res.status,
          });
        }
      } catch (err) {
        logExceptionToSentry(err, {
          context: "setStylistSiteCartAttribute",
          siteName,
        });
      }
    };

    // ---------------------------------------------------------------------------
    // Dropping a stylist must clear the STYLIST CART ATTRIBUTES (CAB-3494)
    //
    // The checkout extension reads its stylist from the CART ATTRIBUTES —
    // StylistID above all — not from localStorage and not from the checkout URL.
    // customCheckout (global-variables.liquid) writes StylistID / StylistType /
    // the show set onto the cart on the way to checkout, and they then LIVE ON
    // THE CART. So clearing localStorage alone left the stylist the shopper had
    // just walked away from sitting on the cart, and any route to checkout that
    // does not run customCheckout — a native cart submit, a dynamic checkout or
    // Shop Pay button, a direct /checkout link, a resumed cart in another tab —
    // handed that stylist straight to the extension.
    //
    // Only StylistSiteName was being cleared here before, which is why StylistID
    // survived "continue without a stylist".
    //
    // An empty value is how Shopify removes a cart attribute.
    // ---------------------------------------------------------------------------
    // Stylist attributes ONLY. The show set (ShowID / ShowName / ShowStatus /
    // EventID) is deliberately left on the cart: dropping a stylist is not
    // leaving a show, so that decision stays with the show flow. `context` is
    // left too — it is a mixed blob, and removing it would take showId/showSite
    // with it. NOTE: it still embeds the dropped stylistId; customCheckout
    // rewrites it wholesale on the next checkout it runs.
    const STYLIST_CART_ATTRS = [
      // What the checkout extension actually reads.
      "StylistID",
      "StylistType",
      STYLIST_SITE_ATTR,
      // Provenance for the stylist that has just been dropped.
      "StylistAttributionSource",
    ];

    // force:true skips the "did we ever write?" shortcut below. That flag is
    // per-browser localStorage, but the attributes live on the CART — which for a
    // logged-in shopper is server-side and shared across devices — so a stylist
    // written elsewhere, or surviving a cleared localStorage, is invisible here
    // and the shortcut would leave it on the cart. A deliberate opt-out has to
    // clear unconditionally; ordinary page loads keep the shortcut.
    const clearStylistCartAttributes = async ({ force = false } = {}) => {
      try {
        // Nothing was written, so there is nothing to undo — and no reason to
        // POST to the cart on page loads that never had a stylist.
        if (!force && !localStorage.getItem(STYLIST_SITE_WRITTEN_KEY)) return;

        const cartUrl =
          (window.theme && window.theme.routes && window.theme.routes.cart) || "/cart";

        const attributes = {};
        STYLIST_CART_ATTRS.forEach((attr) => {
          attributes[attr] = "";
        });

        const res = await fetch(`${cartUrl}/update.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ attributes }),
        });

        if (res.ok) {
          localStorage.removeItem(STYLIST_SITE_WRITTEN_KEY);
          logToSentry("Stylist cart attributes cleared", "info", { force });
        } else {
          logToSentry("Failed to clear stylist cart attributes", "warning", {
            status: res.status,
            force,
          });
        }
      } catch (err) {
        logExceptionToSentry(err, { context: "clearStylistCartAttributes", force });
      }
    };

    // Fires for every stylist resolution/selection across the theme.
    document.addEventListener("theme:partner:render", (event) => {
      const siteName = event?.detail?.partner?.siteName;
      if (siteName) {
        setStylistSiteCartAttribute(siteName);
        return;
      }
      clearStylistCartAttributes();
    });

    // Exposed for the paths where dropping the stylist is an explicit shopper
    // decision rather than a resolution outcome — "continue without a stylist"
    // (partner-selector.liquid) — so they can force the clear through.
    window.clearStylistCartAttributes = clearStylistCartAttributes;

    const getCurrentPWS = async () => {
      const urlParams = new URLSearchParams(window.location.search);

      // Drop stylist attribution that has outlived the configured TTL before any
      // of the stored-value fallbacks below can read it (CAB-3520).
      expireStaleAttribution();

      const fallbackPWS = sessionStorage.getItem("404_PATH")?.split("/")?.at(1);
      sessionStorage.removeItem("404_PATH");
      if (fallbackPWS?.length) {
        logToSentry(`Resolved PWS from 404 fallback: ${fallbackPWS}`, 'info', { fallbackPWS });
        setAttributionSource(ATTRIBUTION_SOURCES.VANITY_PATH);
        return fallbackPWS;
      }

      // A show in the URL wins over a stored one; otherwise re-check the stored
      // show so a show that closes between visits stops attributing (CAB-2840).
      const showIdFromUrl = urlParams.get("show");
      const showIdFromStorage = localStorage.getItem("CURRENT_SHOW_ID");
      const showId = showIdFromUrl || showIdFromStorage;

      if (showId && window.backend) {
        const previousShowId = localStorage.getItem("CURRENT_SHOW_ID");
        const previousShowSite = localStorage.getItem("CURRENT_SHOW_SITE");
        const { isOpen, partner, show } = await validateShow(showId);

        if (isOpen && partner?.siteName?.length) {
          // Switching to a different show must not leave the previous show's
          // name/hostess behind.
          if (previousShowId && (previousShowId !== showId || previousShowSite !== partner.siteName)) {
            // Replacing one show's attribution with another's changes what the
            // order is credited to, so it is reported rather than silent.
            logToSentry('Show attribution replaced by a different show', 'info', {
              showId,
              previousShowId,
              previousShowSite,
              newShowSite: partner.siteName,
            });
            clearShowAttribution(previousShowId);
          }

          persistShowAttribution(showId, partner, show);
          isFromShowUrl = true;
          setAttributionSource(
            showIdFromUrl ? ATTRIBUTION_SOURCES.SHOW : ATTRIBUTION_SOURCES.SHOW_STORED
          );
          logToSentry(`Resolved PWS from Show ID: ${partner.siteName}`, 'info', {
            showId,
            fromUrl: !!showIdFromUrl,
            partner: { siteName: partner.siteName, stylistId: partner.stylistId },
            show: show ? { name: show.name, hostessName: show.hostessName } : null,
            previousShowId,
            previousShowSite
          });
          return partner.siteName;
        }

        // Show is closed or unusable. Clear every trace of it, take the ?show=
        // param out of the address bar so it cannot re-pin itself on the next
        // load (CAB-3515), and fall back to the show stylist's own PWS when we
        // know who that is rather than dropping attribution entirely.
        clearShowAttribution(showId);
        removeShowParamFromUrl();

        if (partner?.siteName?.length) {
          logToSentry('Show unusable - falling back to show stylist PWS', 'info', {
            attribution_event: 'show:closed-fallback',
            showId,
            isClosed: show?.isClosed ?? null,
            siteName: partner.siteName
          });
          setAttributionSource(ATTRIBUTION_SOURCES.SHOW_CLOSED_FALLBACK);
          document.dispatchEvent(
            new CustomEvent("theme:show:closed", {
              detail: { showId, partner, show: show || null },
            })
          );
          return partner.siteName;
        }

        logToSentry(`PWS resolution by Show ID returned no partner`, 'warning', {
          attribution_event: 'show:closed-no-partner',
          showId,
          partner,
          show,
        });
        document.dispatchEvent(
          new CustomEvent("theme:show:closed", {
            detail: { showId, partner: null, show: show || null },
          })
        );
      }

      const paramPWS = urlParams.get("pws");
      if (paramPWS) {
        logToSentry(`Resolved PWS from URL parameter: ${paramPWS}`, 'info', { paramPWS });
        setAttributionSource(ATTRIBUTION_SOURCES.PWS_PARAM);
        return paramPWS;
      }

      // Fallback: check referrer URL for pws param
      try {
        const referrerUrl = document.referrer || sessionStorage.getItem("TRACKING_REFERRER");
        if (referrerUrl) {
          const referrerParams = new URL(referrerUrl).searchParams;
          const referrerPWS = referrerParams.get("pws");
          if (referrerPWS) {
            logToSentry(`Resolved PWS from referrer URL parameter: ${referrerPWS}`, 'info', { referrerPWS, referrerUrl });
            setAttributionSource(ATTRIBUTION_SOURCES.REFERRER);
            return referrerPWS;
          }
        }
      } catch (e) {
        // referrer URL may be invalid or cross-origin, ignore
        logExceptionToSentry(e, { context: 'getCurrentPWS: parsing referrer URL' });
      }

      const localPWS = localStorage.getItem("CURRENT_SITE");
      if (localPWS) {
        logToSentry(`Resolved PWS from localStorage: ${localPWS}`, 'info', { localPWS });
        // Preserve the original source across visits where possible — "stored" on
        // its own tells an investigator nothing about how it got there.
        setAttributionSource(localStorage.getItem(ATTRIBUTION_SOURCE_KEY) || ATTRIBUTION_SOURCES.STORED);
        return localPWS;
      }

      logToSentry('No current PWS could be resolved from context', 'warning');
      return null;
    };

    // The stylist recorded ON THE CUSTOMER'S ACCOUNT (the preferred_stylist_id
    // metafield), resolved to a site name. Deliberately independent of
    // sessionStorage PREFERRED_SITE, which only means "the last stylist resolved
    // in this tab" and would shadow the account's real answer — which is why the
    // stylist-account branch below calls this rather than getPreferredPWS().
    // Returns null when there is none, when the lookup fails, or when that
    // stylist is a former partner (CAB-3494); a failed lookup is not treated as
    // termination.
    const resolveOnFilePreferredPWS = async () => {
      const preferredStylistId = window?.customer?.preferredStylistId;
      if (!preferredStylistId || !window.backend) return null;

      const partner = await window.backend.getPartnerByStylistId(preferredStylistId).catch((err) => {
        logExceptionToSentry(err, { context: 'resolveOnFilePreferredPWS: getPartnerByStylistId', preferredStylistId });
        return null;
      });

      if (isFormerPartner(partner)) {
        logToSentry('Preferred stylist on file is a former partner - treating as no preferred stylist', 'warning', {
          preferredStylistId,
          partner: { siteName: partner.siteName, stylistId: partner.stylistId, status: partner.status },
        });
        return null;
      }

      return partner?.siteName?.length ? partner.siteName : null;
    };

    // Neither source of a "preferred" stylist is allowed to return a terminated
    // one. Both used to: PREFERRED_SITE is re-written on every resolution and
    // survives the stylist being terminated mid-session, and the metafield keeps
    // pointing at them until the backend clears it. A terminated stylist handed
    // back from here is what put them in the chooser labelled "Your Preferred
    // Stylist" and pre-selected (CAB-3494). A failed lookup returns null, which
    // is NOT treated as termination — we leave the preferred stylist alone rather
    // than dropping them because the network hiccuped.
    const getPreferredPWS = async () => {
      const sessionPWS = sessionStorage.getItem("PREFERRED_SITE");
      if (sessionPWS) {
        const sessionPartner = window.backend
          ? await window.backend.getPartnerBySite(sessionPWS).catch((err) => {
              logExceptionToSentry(err, { context: 'getPreferredPWS: getPartnerBySite', sessionPWS });
              return null;
            })
          : null;

        if (isFormerPartner(sessionPartner)) {
          sessionStorage.removeItem("PREFERRED_SITE");
          logToSentry('Stored preferred PWS is a former partner - discarded', 'warning', { sessionPWS });
        } else {
          logToSentry(`Preferred PWS from sessionStorage: ${sessionPWS}`, 'info', { sessionPWS });
          return sessionPWS;
        }
      }

      const onFilePWS = await resolveOnFilePreferredPWS();
      if (onFilePWS) {
        logToSentry(`Preferred PWS from Customer Stylist ID: ${onFilePWS}`, 'info', {
          preferredStylistId: window?.customer?.preferredStylistId ?? null,
          siteName: onFilePWS,
        });
        return onFilePWS;
      }

      return null;
    };

    const loadTrackingInfo = () => {
      if (!sessionStorage.getItem("TRACKING_LANDING_URL")) {
        sessionStorage.setItem("TRACKING_LANDING_URL", window.location.href);
      }

      if (!sessionStorage.getItem("TRACKING_REFERRER") && document.referrer) {
        sessionStorage.setItem("TRACKING_REFERRER", document.referrer);
      }
    };

    function updateUrlPWS(pws) {
      const urlParams = new URLSearchParams(window.location.search);
      const showId = localStorage.getItem("CURRENT_SHOW_ID");
      urlParams.set("pws", pws);
      if (showId) {
        urlParams.set("show", showId);
      } else {
        // Without this the ?show= param survives in the address bar after the show
        // has been cleared, and getCurrentPWS reads it back on the next load —
        // a self-sustaining loop the shopper cannot escape (CAB-3515, CAB-3516).
        urlParams.delete("show");
      }

      // Preserve the hash value if it exists
      const hashValue = window.location.hash;

      // Update URL while maintaining the hash
      window.history.replaceState({}, "", window.location.pathname + "?" + urlParams.toString() + hashValue);
    }

    // visitedPWS   - the stylist whose link/page is being looked at right now.
    // incumbentPWS - the stylist to weigh that against: the one the shopper is
    //                CURRENTLY ATTRIBUTED to when that is someone else, else
    //                their preferred stylist. Chosen by the caller; see the
    //                DOMContentLoaded handler for why both sources are needed.
    async function handlePWSMismatch(visitedPWS, incumbentPWS) {
      if (!window.backend) {
        logToSentry('Cannot handle PWS mismatch - window.backend not available', 'error');
        console.error('[Attribution] Cannot handle PWS mismatch - window.backend not available');
        return;
      }

      const [visitedPartner, incumbentPartner] = await Promise.all([
        window.backend.getPartnerBySite(visitedPWS),
        window.backend.getPartnerBySite(incumbentPWS),
      ]).catch((err) => {
        logExceptionToSentry(err, { context: 'handlePWSMismatch: fetch partners', visitedPWS, incumbentPWS });
        return [null, null];
      });

      if (!(visitedPartner && incumbentPartner)) {
        logToSentry('PWS mismatch details could not be fully loaded', 'warning', { visitedPWS, incumbentPWS, visitedPartner, incumbentPartner });
        return;
      }

      // Never put a terminated stylist in the chooser. Picking them would write
      // an attribution that the next page load has to strip again, dropping the
      // shopper into the unavailable dialog for a stylist they never had a
      // relationship with. The incumbent simply stands instead (CAB-3494) — and if
      // the incumbent is terminated too, handlePartnerResolution withholds
      // attribution and raises the unavailable dialog, which is the right outcome.
      // (Preferred stylists are already filtered by getPreferredPWS; an incumbent
      // read from CURRENT_SITE is not, hence relying on that check downstream.)
      if (isFormerPartner(visitedPartner)) {
        logToSentry('Visited stylist is a former partner - keeping incumbent stylist', 'warning', {
          visitedPWS,
          incumbentPWS,
          visitedPartner: { siteName: visitedPartner.siteName, status: visitedPartner.status },
        });
        await handlePartnerResolution(incumbentPWS, incumbentPWS);
        return;
      }

      logToSentry('PWS Mismatch detected between visited and incumbent stylist', 'warning', {
        visitedPWS,
        incumbentPWS,
        visitedPartner: { siteName: visitedPartner.siteName, stylistId: visitedPartner.stylistId, displayName: visitedPartner.displayName },
        incumbentPartner: { siteName: incumbentPartner.siteName, stylistId: incumbentPartner.stylistId, displayName: incumbentPartner.displayName }
      });

      // detail.visited   - stylist whose page this is
      // detail.incumbent - stylist the chooser must offer as the alternative
      // (was {current, preferred}; renamed because "preferred" no longer describes
      // what this side of the comparison holds — CAB-3508.)
      document.dispatchEvent(
        new CustomEvent("theme:partner:mismatch", {
          detail: { visited: visitedPartner, incumbent: incumbentPartner },
        })
      );
    }

    async function handlePartnerResolution(currentPWS, preferredPWS) {
      if (!window.backend) {
        logToSentry('Cannot resolve partner - window.backend not available', 'error');
        console.error('[Attribution] Cannot resolve partner - window.backend not available');
        return;
      }

      const currentOrPreferredPWS = currentPWS || preferredPWS;
      if (!currentOrPreferredPWS) {
        logToSentry('Partner resolution skipped - no current or preferred PWS provided', 'warning');
        return;
      }

      const partner = await window.backend.getPartnerBySite(
        currentOrPreferredPWS
      ).catch((err) => {
        logExceptionToSentry(err, { context: 'handlePartnerResolution: getPartnerBySite', currentOrPreferredPWS });
        return null;
      });

      if (!partner?.siteName?.length) {
        logToSentry('Partner resolution failed - no partner found for site', 'error', { currentOrPreferredPWS });
        return;
      }

      // A terminated / pending-termination stylist must not be attributed. This
      // check used to live in the DOMContentLoaded handler AFTER this function had
      // already written the CURRENT_* keys, so the terminated stylist still went to
      // checkout as attributes[StylistID], and every page load silently re-wrote
      // the keys that "Continue Without Stylist" had just cleared — which is why
      // the dialog kept coming back on the product page, on add-to-cart and at
      // checkout (CAB-3494). Nothing is persisted here: we clear instead, then let
      // the shopper choose. Stylists previewing their own PWS are exempt, since
      // clearing their own site would break that preview.
      if (isFormerPartner(partner) && !window?.customer?.isStylist) {
        logToSentry('Resolved partner is a former partner - attribution withheld', 'warning', {
          partner: { siteName: partner.siteName, stylistId: partner.stylistId, status: partner.status },
          currentPWS,
          preferredPWS,
        });

        clearStylistAttribution();
        removePwsParamFromUrl();

        window.partner = null;
        document.dispatchEvent(new CustomEvent("theme:partner:render", { detail: { partner: null } }));

        if (hasStylistOptOut()) {
          logToSentry('Unavailable-stylist dialog suppressed - shopper opted out', 'info', {
            siteName: partner.siteName,
          });
          return;
        }

        document.dispatchEvent(
          new CustomEvent("theme:partner:unavailable", {
            detail: { partner },
          })
        );
        return;
      }

      // -------------------------------------------------------------------------
      // "Continue without a stylist" must survive the next page load (CAB-3494)
      //
      // The opt-out only ever suppressed the unavailable DIALOG. Resolution itself
      // carried on, so on the very next page load the stored / referrer /
      // preferred-stylist fallbacks re-wrote CURRENT_* below and the
      // theme:partner:render dispatch put the stylist back onto the cart — the
      // shopper's choice undone with no selection of their own, and StylistID
      // waiting for the extension at checkout again.
      //
      // While the opt-out stands, only an EXPLICIT request may re-attribute:
      // clicking a stylist's link (?pws=), landing on their vanity path, or
      // entering a show. Those are selections; a stored value, a referrer or the
      // account metafield are not. The explicit paths clear the opt-out via
      // clearStylistOptOut() further down, so a real choice ends it immediately.
      // -------------------------------------------------------------------------
      const isExplicitStylistRequest =
        isFromShowUrl ||
        attributionSource === ATTRIBUTION_SOURCES.PWS_PARAM ||
        attributionSource === ATTRIBUTION_SOURCES.VANITY_PATH;

      if (hasStylistOptOut() && !isExplicitStylistRequest) {
        logToSentry("Attribution withheld - shopper continued without a stylist", "warning", {
          partner: { siteName: partner.siteName, stylistId: partner.stylistId },
          currentPWS,
          preferredPWS,
          attributionSource,
        });

        clearStylistAttribution();
        removePwsParamFromUrl();

        window.partner = null;
        document.dispatchEvent(new CustomEvent("theme:partner:render", { detail: { partner: null } }));
        return;
      }

      // A customer's PREFERRED stylist must never change just because they looked
      // at a page. Per the rule confirmed on CAB-3494 it may only change when they
      // have none yet AND complete checkout, or when the current one is
      // terminated — neither of which is knowable here. This page-view sync is the
      // most likely mechanism behind preferred stylists silently flipping
      // (CAB-3503 / CAB-3524), so it is gated off pending the product decision on
      // where the checkout-time write should live.
      const SYNC_PREFERRED_STYLIST_ON_PAGE_VIEW = false;

      let syncedPreferredStylist = false;
      if (
        SYNC_PREFERRED_STYLIST_ON_PAGE_VIEW &&
        window?.customer &&
        window.customer?.customerType !== "Stylist" &&
        window.customer?.preferredStylistId === null
      ) {
        Promise.resolve(window.backend.syncPreferredStylist(partner.siteName)).catch((err) => {
          logExceptionToSentry(err, { context: 'handlePartnerResolution: syncPreferredStylist', siteName: partner.siteName });
        });
        window.customer.preferredStylistId = partner?.stylistId;
        syncedPreferredStylist = true;
      } else if (
        window?.customer &&
        window.customer?.customerType !== "Stylist" &&
        window.customer?.preferredStylistId === null
      ) {
        // Recorded so we can see how often this path is hit before deciding where
        // the write belongs.
        logToSentry('Skipped page-view preferred-stylist sync (gated)', 'info', {
          siteName: partner.siteName,
          stylistId: partner.stylistId,
        });
      }

      updateUrlPWS(partner.siteName);

      // If the PWS came from the preferred-stylist metafield rather than any
      // explicit signal, record that rather than whatever getCurrentPWS last saw.
      const resolvedSource =
        (!currentPWS && preferredPWS) ? ATTRIBUTION_SOURCES.PREFERRED_STYLIST : attributionSource;

      localStorage.setItem("CURRENT_SITE", partner.siteName);
      localStorage.setItem("CURRENT_STYLIST_ID", partner.stylistId);
      localStorage.setItem("CURRENT_STYLIST_TYPE", window.backend.deriveStylistType(partner.tags));
      // Shopping with a stylist again is the opposite of "continue without one",
      // so the opt-out is spent. Without this it would keep suppressing the
      // unavailable dialog for a shopper who has since chosen someone new.
      clearStylistOptOut();
      if (resolvedSource) {
        localStorage.setItem(ATTRIBUTION_SOURCE_KEY, resolvedSource);
      }
      // Stamp only when the attributed stylist actually changes, so simply
      // browsing does not keep pushing the expiry window out (CAB-3520).
      if (localStorage.getItem("CURRENT_ATTRIBUTED_SITE_STAMP") !== partner.siteName) {
        localStorage.setItem(ATTRIBUTION_SET_AT_KEY, String(Date.now()));
        localStorage.setItem("CURRENT_ATTRIBUTED_SITE_STAMP", partner.siteName);
      }
      sessionStorage.setItem("PREFERRED_SITE", partner.siteName);

      window.partner = partner;
      // Disable affinity update
      // document.dispatchEvent(
      //   new CustomEvent("theme:partner:update", { detail: { partner } })
      // );

      logToSentry('Partner resolved successfully', 'info', {
        partner: { siteName: partner.siteName, stylistId: partner.stylistId, status: partner.status },
        currentPWS,
        preferredPWS,
        attributionSource: resolvedSource,
        syncedPreferredStylist,
        customer: window?.customer ? { id: window.customer.customerId, type: window.customer.customerType } : null
      });

      document.dispatchEvent(new CustomEvent("theme:partner:render", { detail: { partner } }));
    }

    document.addEventListener("DOMContentLoaded", async () => {
      // Defensive check: Ensure backend API is available
      if (!window.backend) {
        logToSentry('window.backend is not available on DOMContentLoaded', 'error');
        console.error('[Attribution] window.backend is not available. Backend.js may not be loaded yet.');
        return;
      }

      logToSentry('Starting attribution resolution process', 'info', {
        url: window.location.href,
        referrer: document.referrer,
        customer: window?.customer ? { id: window.customer.customerId, isStylist: window.customer.isStylist, siteName: window.customer.siteName } : null
      });

      loadTrackingInfo();
      const currentPWS = await getCurrentPWS();
      const preferredPWS = await getPreferredPWS();

      if (window?.customer && window.customer?.isStylist) {
        // ---------------------------------------------------------------------
        // A stylist is also a shopper
        //
        // This branch used to pin attribution to her OWN site on every page load
        // (updateUrlPWS + CURRENT_SITE + CURRENT_STYLIST_ID = self), and
        // global-variables.liquid overwrote her account's preferred_stylist_id
        // with her own stylist_id so the stylist she shops with was invisible to
        // JS. Two symptoms followed: logging in swung the PWS to herself, and no
        // stylist change could ever stick — the finder wrote the new stylist, the
        // next page load put the old id straight back into ?pws= and the header.
        //
        // The branch no longer overrides real attribution:
        //   1. Attribution naming someone else — a stylist's link, a referrer, a
        //      show, or her own pick in the finder — is honoured as-is. Whatever
        //      source getCurrentPWS recorded stands.
        //   2. Attribution naming HERSELF is honoured too, whatever the source
        //      (?pws=, vanity path, show, referrer, stored, or the finder).
        //   3. Only when nothing points anywhere is the stylist on her account
        //      attributed, and her own site when there is none on file.
        //
        // (2) used to require an explicit ?pws= / vanity / show visit, treating a
        // stored or referrer value of her own site as the old self-pin echoing
        // back. But a stylist's preferred_stylist_id is very often her sponsor /
        // team leader — she was that stylist's customer before enrolling and the
        // metafield is never reset — so the next page load (no ?pws=, own site
        // only in the referrer / CURRENT_SITE) swung her own PWS and her own
        // finder pick to the sponsor, and "Shopping with" could never be set back
        // to herself (CAB-3917). A stylist who wants to shop with someone else
        // picks them once and (1) keeps it.
        // ---------------------------------------------------------------------
        const selfPWS = window.customer.siteName;
        const onFilePreferredPWS = await resolveOnFilePreferredPWS();

        const attributedElsewhere = !!currentPWS && currentPWS !== selfPWS;
        const onOwnSite = !!selfPWS && currentPWS === selfPWS;

        let targetPWS = selfPWS;
        if (attributedElsewhere) {
          targetPWS = currentPWS;
        } else if (!currentPWS && onFilePreferredPWS) {
          targetPWS = onFilePreferredPWS;
        }

        logToSentry('User is Stylist - resolving attribution for a stylist account', 'info', {
          siteName: selfPWS,
          stylistId: window.customer.stylistId,
          preferredStylistId: window.customer.preferredStylistId ?? null,
          onFilePreferredPWS,
          currentPWS,
          attributionSource,
          attributedElsewhere,
          onOwnSite,
          isFromShowUrl,
          targetPWS,
        });

        if (targetPWS === selfPWS) {
          setAttributionSource(ATTRIBUTION_SOURCES.STYLIST_SELF);
          updateUrlPWS(selfPWS);
          localStorage.setItem("CURRENT_SITE", selfPWS);
          // window.customer.stylistId is now exposed by global-variables.liquid; the
          // "NONE" fallback previously fired for every stylist and was only corrected
          // if the partner lookup below succeeded.
          localStorage.setItem("CURRENT_STYLIST_ID", window.customer?.stylistId ?? "NONE");
        } else if (targetPWS === onFilePreferredPWS && !attributedElsewhere) {
          // Only the fallback case re-labels the source; attribution that came
          // from a link, a show or the finder keeps the provenance getCurrentPWS
          // already recorded.
          setAttributionSource(ATTRIBUTION_SOURCES.PREFERRED_STYLIST);
        }

        // Nothing is pre-written for the two non-self cases on purpose:
        // handlePartnerResolution writes CURRENT_SITE, CURRENT_STYLIST_ID and
        // CURRENT_STYLIST_TYPE from one resolved partner and updates the URL.
        // Writing the site here and the id there is exactly how those keys drift
        // apart, and checkout reads the id.
        await handlePartnerResolution(targetPWS, targetPWS);
      } else {
        // ---------------------------------------------------------------------
        // Which stylist does the visited one get weighed against? (CAB-3508)
        //
        // It used to be preferredPWS alone, i.e. sessionStorage PREFERRED_SITE
        // ("the last stylist resolved in this tab") or the on-file metafield.
        // Neither follows a consent-suppressed switch: handlePartnerResolution —
        // the only writer of PREFERRED_SITE — never runs while the mismatch
        // branch is active, so after consenting to B the session still said A.
        // Visiting C then offered A vs C instead of B vs C, and visiting A again
        // looked like no change at all and switched silently (CAB-3508 QA 17 Aug,
        // and Juliano's 13 Aug dev-test note).
        //
        // CURRENT_SITE is the authoritative answer: it is the stylist attribution
        // actually stands on, the one CURRENT_STYLIST_ID accompanies and checkout
        // credits as attributes[StylistID]. So compare against that whenever it
        // names someone other than the stylist being visited.
        //
        // preferredPWS is still the fallback, and that is not cosmetic: once the
        // shopper is attributed to the stylist they are visiting (attributed ===
        // visited) it is the only remaining difference to detect, which is what
        // brings the dialog back when consent for that stylist expires — the
        // "revert to the original stylist" this ticket asks for. Preferred keeps
        // driving the labelling and the default choice inside the dialog.
        // ---------------------------------------------------------------------
        const attributedPWS = localStorage.getItem("CURRENT_SITE");
        const incumbentPWS =
          attributedPWS && attributedPWS !== currentPWS ? attributedPWS : preferredPWS;

        const isDifferentPWS = currentPWS && incumbentPWS && currentPWS !== incumbentPWS;

        // If landing from a show URL, always use the show's stylist without showing the modal
        if (isDifferentPWS && !isFromShowUrl) {
          logToSentry('PWS Mismatch with non-show landing - handling mismatch modal', 'info', {
            currentPWS,
            incumbentPWS,
            attributedPWS,
            preferredPWS,
            incumbentIsAttributed: incumbentPWS === attributedPWS,
          });
          await handlePWSMismatch(currentPWS, incumbentPWS);
        } else {
          if (isDifferentPWS && isFromShowUrl) {
            logToSentry('PWS Mismatch but landed from show URL - bypassing mismatch modal', 'info', { currentPWS, incumbentPWS, attributedPWS, preferredPWS });
          }
          // The former-partner case is handled inside handlePartnerResolution now,
          // before anything is persisted — see the isFormerPartner branch there.
          await handlePartnerResolution(currentPWS, preferredPWS);
        }
      }
    });
  } catch (e) {
    if (typeof logExceptionToSentry === 'function') {
      logExceptionToSentry(e, { context: 'Outer attribution logic try-catch block' });
    }
    console.error(e);
  }
})();
